import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "409",
  "slug": "geography-sub-plans-no-prep",
  "title": "Geography Sub Plans: No-Prep Lessons a Substitute Can Actually Run",
  "excerpt": "A good emergency sub plan should run itself — no prep, no special logins, no chaos. Here are no-prep geography lessons you can leave for any substitute teacher.",
  "date": "2026-06-11",
  "category": "Education",
  "image": "https://images.unsplash.com/photo-1521295121783-8a321d551ad2?ixid=M3w5NjY4NzN8MHwxfHNlYXJjaHwxfHxjbGFzc3Jvb20lMjBnbG9iZSUyMGRlc2t8ZW58MXwwfHx8MTc4MTE2NTU0M3ww&ixlib=rb-4.1.0&w=800&q=80&fit=crop",
  "readTime": "4 min",
  "author": "EarthGuessr Team",
  "tldr": "The best geography sub plans are self-running: clear instructions, no prep, no special logins, and engaging enough to keep a class on task without the regular teacher. Reliable options include map scavenger hunts, blank-map labeling races, current-events geography, country one-pagers, geography bingo, and a guess-the-location game like EarthGuessr.",
  "content": [
    {
      "type": "paragraph",
      "text": "Every teacher knows the feeling: you wake up sick, or get called away, and now you need a lesson that a substitute who may not know any geography can run without the class descending into chaos. A great sub plan has nothing to do with the cleverness of the activity and everything to do with whether it runs itself. Here is what makes one work, plus a handful of no-prep geography lessons you can leave in the folder and forget about."
    },
    {
      "type": "heading",
      "text": "What makes a sub plan actually work"
    },
    {
      "type": "paragraph",
      "text": "Before the activities, the principles. A sub plan that survives contact with a real classroom usually checks these boxes:"
    },
    {
      "type": "list",
      "items": [
        "Zero prep — the substitute should not have to print, log in, or set anything up beyond handing out a sheet or opening one website.",
        "Self-explanatory — the instructions should make sense to someone who has never taught geography.",
        "Hard to derail — the activity should keep students busy individually or in small groups, not depend on a lecture.",
        "Standards-adjacent — even an emergency lesson should reinforce real map and geography skills, not just fill time.",
        "A built-in finish — there should be something to hand in or a clear endpoint, so the class stays accountable."
      ]
    },
    {
      "type": "heading",
      "text": "1. The blank-map labeling race"
    },
    {
      "type": "paragraph",
      "text": "Leave a stack of blank continent or country maps and an atlas or textbook per table. Students race, individually or in pairs, to label as many countries, capitals, rivers, or mountain ranges as they can in the time allowed. It is self-paced, self-checking against the atlas, and endlessly adaptable to whatever region the class is studying. The substitute just hands out the sheets and keeps time."
    },
    {
      "type": "heading",
      "text": "2. The map scavenger hunt"
    },
    {
      "type": "paragraph",
      "text": "Write a list of twenty things to find on a world map or in an atlas: a country that crosses the equator, a landlocked nation, the longest river on a given continent, a capital city that shares its name with its country. Students hunt them down and write the answers. It rewards close reading of a map, needs nothing but the atlas already in the room, and the answer key lets the sub confirm results without knowing the material."
    },
    {
      "type": "heading",
      "text": "3. Current-events geography"
    },
    {
      "type": "paragraph",
      "text": "Have students find a news story from anywhere in the world, locate the place on a map, and write a short paragraph on where it is, what its geography is like, and why that geography matters to the story. It connects geography to the real world, works at almost any grade level, and the output is a tidy piece of writing the regular teacher can assess later."
    },
    {
      "type": "heading",
      "text": "4. Country one-pagers"
    },
    {
      "type": "paragraph",
      "text": "Assign each student a country — or let them choose — and have them build a single illustrated page covering its location, capital, physical features, climate, and one surprising fact. It is calm, independent work that produces something worth keeping, and it quietly drills the habit of researching a place systematically."
    },
    {
      "type": "heading",
      "text": "5. Geography bingo and a guess-the-location game"
    },
    {
      "type": "paragraph",
      "text": "For the last stretch of class, when focus fades, lighter activities keep order. Geography bingo — capitals, flags, or landmarks on the cards — runs on a single projected list. And a guess-the-location game like EarthGuessr is ideal for a substitute: project one round, let the class debate the clues together, and reveal the answer. It needs no preparation, it is genuinely fun, and it sneaks in real skills — reading landscapes, coastlines, and climate cues to work out where on Earth a place is."
    },
    {
      "type": "heading",
      "text": "Scaling activities up and down by grade"
    },
    {
      "type": "paragraph",
      "text": "Write a basic version and an extension for each activity, and specify the age group and material already taught. Leave the substitute the maps, answer key, and timing so the lesson can run without extra research."
    },
    {
      "type": "heading",
      "text": "Leave it ready to go"
    },
    {
      "type": "paragraph",
      "text": "The final piece of advice is logistical: keep a printed 'emergency geography folder' in your desk with two or three of these ready to hand out, plus class rosters and seating charts. The activity matters less than the fact that the substitute can pick it up cold and run it. Get that right and a surprise absence becomes a normal day for your students — and they might even learn something while you are gone."
    }
  ]
};
